import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, StyledEngineProvider, Snackbar, Button, Alert } from '@mui/material';

import AppRoutes from './routes/AppRoutes';
import NavigationScroll from './layout/NavigationScroll';
import Theme from './themes';
import { NavigationBlockerProvider } from './components/NavigationBlockerProvider';
import { ButtonStyles } from './utils/general';
import { useTheme } from "@mui/styles";

//-----------------------|| APP ||-----------------------//

const App = ({ newVersionAvailable }) => {
    const Customization = useSelector((state) => state.customization);
    const [showUpdateBanner, setShowUpdateBanner] = useState(newVersionAvailable);
    const [showSessionWarning, setShowSessionWarning] = useState(false);
    const [timeLeft, setTimeLeft] = useState(null);
    const theme = useTheme();

    useEffect(() => {
        const checkInterval = setInterval(() => {
            if (localStorage.getItem('new_version_detected') === 'true') {
                setShowUpdateBanner(true);
            }
        }, 5000);
        return () => clearInterval(checkInterval);
    }, []);

    const handleRefresh = () => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload(true);
    };

    const handleSessionLogout = useCallback(() => {
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("tokenExpiry");
        sessionStorage.clear();
        window.location.href = "/login";
    }, []);

    useEffect(() => {
        const expiry = localStorage.getItem("tokenExpiry");
        if (!expiry) return;

        const interval = setInterval(() => {
            const remaining = Number(expiry) - Date.now();

            if (remaining <= 0) {
                handleSessionLogout();
                clearInterval(interval);
                return;
            }

            setTimeLeft(remaining);

            if (remaining <= 60 * 60 * 1000 && !showSessionWarning) {
                setShowSessionWarning(true);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [handleSessionLogout, showSessionWarning]);

    const formatTime = (ms) => {
        if (!ms) return "Loading...";
        const totalSec = Math.floor(ms / 1000);
        const hrs = Math.floor(totalSec / 3600);
        const mins = Math.floor((totalSec % 3600) / 60);
        const secs = totalSec % 60;
        return `${hrs}h ${mins}m ${secs}s`;
    };

    return (
        <NavigationBlockerProvider>
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={Theme(Customization)}>
                    <CssBaseline />
                    <Snackbar
                        open={showUpdateBanner}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <Alert
                            severity="info"
                            sx={{ width: '100%', backgroundColor: '#fff', color: '#1976d2' }}
                            action={
                                <Button
                                    size="small"
                                    onClick={handleRefresh}
                                    style={ButtonStyles(theme)}
                                >
                                    Refresh
                                </Button>
                            }
                        >
                            A new version of the app is available.
                        </Alert>
                    </Snackbar>

                    <Snackbar
                        open={showSessionWarning}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <Alert
                            severity="warning"
                            sx={{ width: '100%', backgroundColor: '#fff3cd', color: '#856404' }}
                            action={
                                <Button
                                    size="small"
                                    onClick={() => setShowSessionWarning(false)}
                                    style={ButtonStyles(theme)}
                                >
                                    Hide
                                </Button>
                            }
                        >
                            ⚠️ Your session is about to expire! Time Left: <strong>{formatTime(timeLeft)}</strong> <br/><br/>
                                Please logout and login again, or it will automatically logout.
                        </Alert>
                    </Snackbar>
                    <NavigationScroll>
                        <AppRoutes />
                    </NavigationScroll>
                </ThemeProvider>
            </StyledEngineProvider>
        </NavigationBlockerProvider>
    );
};

export default App;